import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import clsx from "clsx";
import {
  canAnimateTyping,
  getTypingDelay,
  useChatTyping,
} from "../ChatTurnLayout/ChatTypingContext";
import { TypingIndicator } from "../TypingIndicator";
import styles from "./BotMessage.module.scss";

interface BotMessageProps {
  // One paragraph per entry.
  lines: readonly ReactNode[];
  // An answer the backend (or a check) rejected.
  isError?: boolean;
  // A reply written now, not history: typed out line by line.
  isTyped?: boolean;
}

export function BotMessage({
  lines,
  isError = false,
  isTyped = false,
}: BotMessageProps) {
  const id = useId();
  const typing = useChatTyping();
  const [animates] = useState(() => isTyped && !!typing && canAnimateTyping());
  const [shownCount, setShownCount] = useState(animates ? 0 : lines.length);
  const linesRef = useRef(lines);
  linesRef.current = lines;

  const enqueue = typing?.enqueue;
  const dequeue = typing?.dequeue;
  const isTypedOut = shownCount >= lines.length;

  useLayoutEffect(() => {
    if (!animates || !enqueue || !dequeue) {
      return;
    }
    enqueue(id);
    return () => dequeue(id);
  }, [animates, enqueue, dequeue, id]);

  const isMyTurn = animates && typing?.queue[0] === id;

  useEffect(() => {
    if (!isMyTurn) {
      return;
    }
    if (isTypedOut) {
      dequeue?.(id);
      return;
    }
    const timer = window.setTimeout(() => {
      setShownCount((count) => count + 1);
    }, getTypingDelay(linesRef.current[shownCount]));
    return () => window.clearTimeout(timer);
  }, [isMyTurn, isTypedOut, shownCount, dequeue, id]);

  // Waiting for the messages before it to be typed.
  if (animates && shownCount === 0 && !isMyTurn) {
    return null;
  }

  return (
    <div
      className={clsx(styles.root, isError && styles["root--error"])}
      role={isError ? "alert" : undefined}
    >
      {lines.slice(0, shownCount).map((line, index) => (
        <p key={index} className={styles.line}>
          {line}
        </p>
      ))}
      {isMyTurn && !isTypedOut && <TypingIndicator />}
    </div>
  );
}
